import { useState, useEffect, useCallback, useRef } from "react";

const AUTOSAVE_PREFIX = "tempo_draft_";
const AUTOSAVE_DEBOUNCE_MS = 500;
const MAX_DRAFTS_AGE_HOURS = 24;

/**
 * Hook to autosave form data to localStorage
 * Automatically saves drafts as user makes changes and restores on mount
 */
export function useFormAutosave<T>(
  key: string,
  initialData: T,
  options: {
    enabled?: boolean;
    debounceMs?: number;
    onRestored?: (data: T) => void;
  } = {}
) {
  const { 
    enabled = true, 
    debounceMs = AUTOSAVE_DEBOUNCE_MS,
    onRestored 
  } = options;
  
  const storageKey = `${AUTOSAVE_PREFIX}${key}`;
  const [hasDraft, setHasDraft] = useState(false);
  const [draftData, setDraftData] = useState<T | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitializedRef = useRef(false);

  // Check for existing draft on mount
  useEffect(() => {
    if (!enabled || isInitializedRef.current) return;
    isInitializedRef.current = true;

    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Check if draft is not too old
        if (parsed.timestamp && Date.now() - parsed.timestamp < MAX_DRAFTS_AGE_HOURS * 60 * 60 * 1000) {
          setDraftData(parsed.data as T);
          setHasDraft(true);
        } else {
          // Clean up old draft
          localStorage.removeItem(storageKey);
        }
      }
    } catch (e) {
      console.warn("Failed to load draft:", e);
      localStorage.removeItem(storageKey);
    }
  }, [storageKey, enabled]);

  // Save draft with debounce
  const saveDraft = useCallback((data: T) => {
    if (!enabled) return;

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      try {
        const payload = {
          data,
          timestamp: Date.now(),
        };
        localStorage.setItem(storageKey, JSON.stringify(payload));
        setHasDraft(true);
      } catch (e) {
        console.warn("Failed to save draft:", e);
      }
    }, debounceMs);
  }, [storageKey, debounceMs, enabled]);

  // Clear the draft (call after successful save)
  const clearDraft = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    try {
      localStorage.removeItem(storageKey);
      setHasDraft(false);
      setDraftData(null);
    } catch (e) {
      console.warn("Failed to clear draft:", e);
    }
  }, [storageKey]);

  // Restore draft data - clears draft immediately so banner disappears
  const restoreDraft = useCallback(() => {
    if (draftData) {
      const data = draftData;
      clearDraft(); // Clear immediately so banner disappears
      onRestored?.(data);
      return data;
    }
    return null;
  }, [draftData, onRestored, clearDraft]);

  // Dismiss draft without restoring
  const dismissDraft = useCallback(() => {
    clearDraft();
  }, [clearDraft]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return {
    hasDraft,
    draftData,
    saveDraft,
    clearDraft,
    restoreDraft,
    dismissDraft,
  };
}

/**
 * Clean up all old drafts (call on app init)
 */
export function cleanupOldDrafts() {
  try {
    const now = Date.now();
    const maxAge = MAX_DRAFTS_AGE_HOURS * 60 * 60 * 1000;
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(AUTOSAVE_PREFIX)) {
        try {
          const stored = localStorage.getItem(key);
          if (stored) {
            const parsed = JSON.parse(stored);
            if (!parsed.timestamp || now - parsed.timestamp > maxAge) {
              localStorage.removeItem(key);
            }
          }
        } catch {
          localStorage.removeItem(key!);
        }
      }
    }
  } catch (e) {
    console.warn("Failed to cleanup old drafts:", e);
  }
}

/**
 * Get a unique key for form autosave
 */
export function getAutosaveKey(formType: string, ...identifiers: (string | undefined)[]) {
  return [formType, ...identifiers.filter(Boolean)].join("_");
}
